import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ProgressBar } from 'primereact/progressbar';
import { Rating } from 'primereact/rating';
import { Tag } from 'primereact/tag';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { getSurveysURL } from '../../../utils/urls';
import Layout from '../../core/component/layout';

const CustomerSurveys = () => {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [surveyDialog, setSurveyDialog] = useState(false);
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});
    const [surveyStats, setSurveyStats] = useState({
        avgNPS: 0,
        avgCSAT: 0,
        responseRate: 0,
        totalSurveys: 0
    });

    const surveyTypes = [
        { label: 'All', value: null },
        { label: 'NPS', value: 'nps' },
        { label: 'CSAT', value: 'csat' },
        { label: 'CES', value: 'ces' },
        { label: 'Post Resolution', value: 'post_resolution' },
        { label: 'Follow Up', value: 'follow_up' },
        { label: 'Custom', value: 'custom' }
    ];

    const surveyStatuses = [
        { label: 'All', value: null },
        { label: 'Sent', value: 'sent' },
        { label: 'Opened', value: 'opened' },
        { label: 'Started', value: 'started' },
        { label: 'Completed', value: 'completed' },
        { label: 'Expired', value: 'expired' },
        { label: 'Bounced', value: 'bounced' }
    ];

    useEffect(() => {
        loadSurveys();
    }, []);

    const loadSurveys = async () => {
        setLoading(true);
        try {
            const response = await makeRequest2(getSurveysURL, 'GET');

            console.log('Surveys response:', response);

            if (response.success && response.data) {
                // Handle nested response structure - makeRequest2 wraps response
                // Backend returns { success, data: [...] }
                // makeRequest2 wraps it as { success, data: { success, data: [...] } }
                let surveysData;
                if (response.data.data) {
                    surveysData = response.data.data;
                } else if (Array.isArray(response.data)) {
                    surveysData = response.data;
                } else {
                    surveysData = [];
                }

                setSurveys(Array.isArray(surveysData) ? surveysData : []);

                // Calculate statistics and update chart with actual data
                if (surveysData.length > 0) {
                    const npsScores = surveysData.filter(s => s.nps_score !== null && s.nps_score !== undefined).map(s => s.nps_score);
                    const csatScores = surveysData.filter(s => s.csat_score).map(s => s.csat_score);
                    const completedSurveys = surveysData.filter(s => s.status === 'completed').length;

                    // Calculate NPS categories
                    const promoters = npsScores.filter(score => score >= 9).length;
                    const passives = npsScores.filter(score => score >= 7 && score < 9).length;
                    const detractors = npsScores.filter(score => score < 7).length;

                    setSurveyStats({
                        avgNPS: npsScores.length > 0 ? (npsScores.reduce((a, b) => a + b, 0) / npsScores.length).toFixed(1) : 0,
                        avgCSAT: csatScores.length > 0 ? (csatScores.reduce((a, b) => a + b, 0) / csatScores.length).toFixed(1) : 0,
                        responseRate: surveysData.length > 0 ? Math.round((completedSurveys / surveysData.length) * 100) : 0,
                        totalSurveys: surveysData.length
                    });

                    // Update chart with actual data
                    initChart(promoters, passives, detractors);
                } else {
                    // No data - show empty chart
                    initChart(0, 0, 0);
                }
            } else {
                toastify(response.error || 'Failed to load surveys', 'error');
                setSurveys([]);
                initChart(0, 0, 0);
            }
        } catch (error) {
            console.error('Error loading surveys:', error);
            toastify(error.message || 'Error loading surveys', 'error');
            setSurveys([]);
            initChart(0, 0, 0);
        }
        setLoading(false);
    };

    const initChart = (promoters = 0, passives = 0, detractors = 0) => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color');

        const data = {
            labels: ['Promoters', 'Passives', 'Detractors'],
            datasets: [
                {
                    data: [promoters, passives, detractors],
                    backgroundColor: [
                        documentStyle.getPropertyValue('--green-500') || '#22c55e',
                        documentStyle.getPropertyValue('--yellow-500') || '#eab308',
                        documentStyle.getPropertyValue('--red-500') || '#ef4444'
                    ],
                    hoverBackgroundColor: [
                        documentStyle.getPropertyValue('--green-400') || '#4ade80',
                        documentStyle.getPropertyValue('--yellow-400') || '#facc15',
                        documentStyle.getPropertyValue('--red-400') || '#f87171'
                    ]
                }
            ]
        };

        const options = {
            cutout: '60%',
            maintainAspectRatio: true,
            responsive: true,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: textColor,
                        padding: 10,
                        font: {
                            size: 12
                        }
                    }
                }
            }
        };

        setChartData(data);
        setChartOptions(options);
    };

    const viewSurvey = (survey) => {
        setSelectedSurvey(survey);
        setSurveyDialog(true);
    };

    const hideDialog = () => {
        setSurveyDialog(false);
        setSelectedSurvey(null);
    };

    const statusBodyTemplate = (rowData) => {
        return <Tag value={rowData.status} severity={getStatusSeverity(rowData.status)}></Tag>;
    };

    const typeBodyTemplate = (rowData) => {
        return <Tag value={rowData.survey_type} severity={getTypeSeverity(rowData.survey_type)}></Tag>;
    };

    const npsScoreBodyTemplate = (rowData) => {
        if (rowData.nps_score !== null && rowData.nps_score !== undefined) {
            return (
                <div className="flex align-items-center">
                    <span className="mr-2">{rowData.nps_score}/10</span>
                    <ProgressBar
                        value={rowData.nps_score * 10}
                        className="w-6rem"
                        style={{ height: '8px' }}
                        color={getNPSColor(rowData.nps_score)}
                    />
                </div>
            );
        }
        return <span className="text-muted">N/A</span>;
    };

    const csatRatingBodyTemplate = (rowData) => {
        if (rowData.csat_score) {
            return <Rating value={rowData.csat_score} readOnly stars={5} cancel={false} />;
        }
        return <span className="text-muted">N/A</span>;
    };

    const customerNameBodyTemplate = (rowData) => {
        const customer = rowData.customer_id;
        if (!customer) return 'N/A';

        // Try fullName first, then firstName + lastName
        if (customer.fullName && customer.fullName !== customer.email) {
            return customer.fullName;
        }

        const firstName = customer.firstName || '';
        const lastName = customer.lastName || '';
        const name = `${firstName} ${lastName}`.trim();

        return name || 'N/A';
    };

    const agentNameBodyTemplate = (rowData) => {
        const agent = rowData.agent_id;
        if (!agent) return 'N/A';

        // Try fullName first, then firstName + lastName
        if (agent.fullName && agent.fullName !== agent.email) {
            return agent.fullName;
        }

        const firstName = agent.firstName || '';
        const lastName = agent.lastName || '';
        const name = `${firstName} ${lastName}`.trim();

        return name || 'N/A';
    };

    const completedAtBodyTemplate = (rowData) => {
        return rowData.completed_at ? new Date(rowData.completed_at).toLocaleDateString() : 'Pending';
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-info p-button-text"
                    onClick={() => viewSurvey(rowData)}
                    tooltip="View Details"
                />
            </div>
        );
    };

    const getStatusSeverity = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'started':
                return 'warning';
            case 'sent':
                return 'info';
            case 'expired':
                return 'danger';
            case 'bounced':
                return 'danger';
            default:
                return null;
        }
    };

    const getTypeSeverity = (type) => {
        switch (type) {
            case 'nps':
                return 'info';
            case 'csat':
                return 'success';
            case 'ces':
                return 'warning';
            case 'post_resolution':
                return 'help';
            default:
                return null;
        }
    };

    const getNPSColor = (score) => {
        if (score >= 9) return '#22c55e'; // green
        if (score >= 7) return '#eab308'; // yellow
        return '#ef4444'; // red
    };

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/core/dashboard"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item" aria-current="page">
                                    Customer Surveys
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-12 col-md-4">
                    <div className="card">
                        <div className="card-body">
                            <h6 className="mb-3">NPS Distribution</h6>
                            <div style={{ maxWidth: '300px', margin: '0 auto' }}>
                                <Chart type="doughnut" data={chartData} options={chartOptions} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-8">
                    <div className="card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-6 col-md-3">
                                    <div className="text-center">
                                        <h4 className="mb-0 text-primary">{surveyStats.avgNPS}/10</h4>
                                        <p className="text-muted mb-0">Avg NPS Score</p>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="text-center">
                                        <h4 className="mb-0 text-primary">{surveyStats.avgCSAT}/5</h4>
                                        <p className="text-muted mb-0">Avg CSAT Score</p>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="text-center">
                                        <h4 className="mb-0 text-primary">{surveyStats.responseRate}%</h4>
                                        <p className="text-muted mb-0">Response Rate</p>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="text-center">
                                        <h4 className="mb-0 text-primary">{surveyStats.totalSurveys}</h4>
                                        <p className="text-muted mb-0">Total Surveys</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5>Customer Surveys & Feedback</h5>
                                <div className="d-flex gap-2">
                                    <Dropdown
                                        value={typeFilter}
                                        options={surveyTypes}
                                        onChange={(e) => setTypeFilter(e.value)}
                                        placeholder="Filter by Type"
                                        showClear
                                    />
                                    <Dropdown
                                        value={statusFilter}
                                        options={surveyStatuses}
                                        onChange={(e) => setStatusFilter(e.value)}
                                        placeholder="Filter by Status"
                                        showClear
                                    />
                                    <InputText
                                        type="search"
                                        value={globalFilter}
                                        onChange={(e) => setGlobalFilter(e.value)}
                                        placeholder="Search surveys..."
                                        className="p-inputtext-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <DataTable
                                value={surveys}
                                loading={loading}
                                dataKey="_id"
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 25]}
                                className="datatable-responsive"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} surveys"
                                emptyMessage="No surveys found."
                                globalFilter={globalFilter}
                                filters={{
                                    survey_type: { value: typeFilter, matchMode: 'equals' },
                                    status: { value: statusFilter, matchMode: 'equals' }
                                }}
                                scrollable={false}
                                responsiveLayout="scroll"
                            >
                                <Column field="survey_id" header="Survey ID" sortable style={{ width: '10%' }} />
                                <Column field="survey_type" header="Type" body={typeBodyTemplate} sortable style={{ width: '10%' }} />
                                <Column field="customer_name" header="Customer" body={customerNameBodyTemplate} sortable style={{ width: '15%' }} />
                                <Column field="agent_name" header="Agent" body={agentNameBodyTemplate} sortable style={{ width: '12%' }} />
                                <Column field="nps_score" header="NPS Score" body={npsScoreBodyTemplate} sortable style={{ width: '12%' }} />
                                <Column field="csat_score" header="CSAT Rating" body={csatRatingBodyTemplate} sortable style={{ width: '12%' }} />
                                <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ width: '10%' }} />
                                <Column field="completed_at" header="Completed" body={completedAtBodyTemplate} sortable style={{ width: '12%' }} />
                                <Column body={actionBodyTemplate} header="Actions" style={{ width: '7%' }} />
                            </DataTable>

                            <Dialog
                                visible={surveyDialog}
                                style={{ width: '70vw' }}
                                header="Survey Details & Feedback"
                                modal
                                className="p-fluid"
                                onHide={hideDialog}
                            >
                                {selectedSurvey && (
                                    <div className="grid">
                                        <div className="col-12 md:col-6">
                                            <h6>Survey Information</h6>
                                            <p><strong>Survey ID:</strong> {selectedSurvey.survey_id}</p>
                                            <p><strong>Type:</strong> <Tag value={selectedSurvey.survey_type} severity={getTypeSeverity(selectedSurvey.survey_type)} /></p>
                                            <p><strong>Status:</strong> <Tag value={selectedSurvey.status} severity={getStatusSeverity(selectedSurvey.status)} /></p>
                                            <p><strong>Channel:</strong> {selectedSurvey.survey_channel}</p>

                                            <h6>Scores & Ratings</h6>
                                            {selectedSurvey.nps_score !== null && (
                                                <p><strong>NPS Score:</strong> {selectedSurvey.nps_score}/10 ({selectedSurvey.nps_category})</p>
                                            )}
                                            {selectedSurvey.csat_score && (
                                                <p><strong>CSAT Rating:</strong> <Rating value={selectedSurvey.csat_score} readOnly stars={5} cancel={false} /></p>
                                            )}
                                            {selectedSurvey.overall_rating && (
                                                <p><strong>Overall Rating:</strong> <Rating value={selectedSurvey.overall_rating} readOnly stars={5} cancel={false} /></p>
                                            )}
                                        </div>

                                        <div className="col-12 md:col-6">
                                            <h6>Customer Information</h6>
                                            <p><strong>Name:</strong> {customerNameBodyTemplate(selectedSurvey)}</p>
                                            <p><strong>Email:</strong> {selectedSurvey.customer_id?.email}</p>

                                            <h6>Agent Information</h6>
                                            <p><strong>Agent:</strong> {agentNameBodyTemplate(selectedSurvey)}</p>
                                            <p><strong>Agent ID:</strong> {selectedSurvey.agent_id?.agent_id}</p>
                                            {selectedSurvey.agent_rating && (
                                                <p><strong>Agent Rating:</strong> <Rating value={selectedSurvey.agent_rating} readOnly stars={5} cancel={false} /></p>
                                            )}
                                        </div>

                                        {selectedSurvey.feedback_text && (
                                            <div className="col-12">
                                                <h6>Customer Feedback</h6>
                                                <div className="p-3 surface-100 border-round">
                                                    <em>"{selectedSurvey.feedback_text}"</em>
                                                </div>
                                            </div>
                                        )}

                                        <div className="col-12">
                                            <h6>Timeline</h6>
                                            <div className="flex flex-column gap-2">
                                                <div><strong>Sent:</strong> {new Date(selectedSurvey.sent_at).toLocaleString()}</div>
                                                {selectedSurvey.opened_at && (
                                                    <div><strong>Opened:</strong> {new Date(selectedSurvey.opened_at).toLocaleString()}</div>
                                                )}
                                                {selectedSurvey.completed_at && (
                                                    <div><strong>Completed:</strong> {new Date(selectedSurvey.completed_at).toLocaleString()}</div>
                                                )}
                                                {selectedSurvey.expires_at && (
                                                    <div><strong>Expires:</strong> {new Date(selectedSurvey.expires_at).toLocaleString()}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Dialog>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default CustomerSurveys;